import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import zipObject from 'lodash/zipObject';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import uuid from 'uuid';

import { UIContext } from '@uptime/shared/providers/UIProvider';

import useStyles from '../styles';

import Head from './Head';

const DesktopList = (props) => {
  const {
    order,
    orderBy,
    onRequestSort,
    headCells,
    bodyCells,
    rowStyle = { height: 70 },
    selectable = false,
    customClass,
    tableTitle,
    itemsCount,
    currentPage,
    onRowClicked,
  } = props;

  const {
    selectableTable = {},
    bulkUpdateCounter,
    setSelection,
    setBulkUpdateCounter,
    clearBulkUpdateState,
  } = useContext(UIContext);
  const { option, include = [], exclude = [] } = selectableTable;

  const allRowsOnAPage = bodyCells.map(({ id }) => id);

  const classes = useStyles();

  const cellValue = (val) => {
    if (isNil(val) || val === '') {
      return '-';
    }
    return val;
  };

  const isSelected = (id) => {
    if (option === 'all') {
      return !exclude.includes(id);
    }

    if (option === 'unselected') {
      return include.includes(id);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const numberOfPages = Array.from({ length: Math.ceil(itemsCount / 10) }, (_, i) => i + 1);

      setSelection({
        option: 'all',
        include: [],
        exclude: [],
        pages: zipObject(numberOfPages, numberOfPages),
      });

      setBulkUpdateCounter(itemsCount);
      return;
    }

    clearBulkUpdateState();
  };

  const handleClick = (event, rowId) => {
    if (option === 'all') {
      if (event.target.checked) {
        const toAdd = exclude.filter((id) => id !== rowId);

        const isAllPageSelected =
          allRowsOnAPage.every((id) => toAdd.includes(id)) ||
          (option === 'all' && include.length === 0 && toAdd.length === 0);

        setSelection({
          ...selectableTable,
          exclude: toAdd,
          ...(isAllPageSelected && {
            pages: {
              ...selectableTable.pages,
              [currentPage + 1]: currentPage + 1,
            },
          }),
        });

        setBulkUpdateCounter(bulkUpdateCounter + 1);
        return;
      }

      if (!event.target.checked) {
        const { [currentPage + 1]: _, ...pages } = selectableTable.pages;

        setSelection({
          ...selectableTable,
          exclude: uniq([...exclude, rowId]),
          pages,
        });

        setBulkUpdateCounter(bulkUpdateCounter - 1);
        return;
      }
    }

    if (option === 'unselected') {
      if (event.target.checked) {
        const toAdd = uniq([...include, rowId]);

        const isAllPageSelected = allRowsOnAPage.every((id) => toAdd.includes(id));

        setSelection({
          ...selectableTable,
          include: toAdd,
          ...(isAllPageSelected && {
            pages: {
              ...selectableTable.pages,
              [currentPage + 1]: currentPage + 1,
            },
          }),
        });

        setBulkUpdateCounter(bulkUpdateCounter + 1);
        return;
      }

      if (!event.target.checked) {
        const toRemove = include.filter((id) => id !== rowId);
        const isAllPageSelected = allRowsOnAPage.every((id) => toRemove.includes(id));
        const { [currentPage + 1]: _, ...pages } = selectableTable.pages;

        setSelection({
          ...selectableTable,
          include: include.filter((id) => id !== rowId),

          ...(!isAllPageSelected && {
            pages,
          }),
        });

        setBulkUpdateCounter(bulkUpdateCounter - 1);
        return;
      }
    }
  };

  return (
    <div className={`${classes.tableWrapper} ${tableTitle && classes.tableTitleWrapper} ${customClass}`}>
      <Table stickyHeader>
        <Head
          order={order}
          orderBy={orderBy}
          headCells={headCells}
          rowCount={itemsCount}
          selectable={selectable}
          onRequestSort={onRequestSort}
          onSelectAllClick={handleSelectAllClick}
          tableTitle={tableTitle}
        />
        <TableBody className={classes.tableBody}>
          {bodyCells.map((row) => {
            const isRowSelected = isSelected(row.id);
            return (
              <TableRow
                hover
                style={rowStyle}
                key={uuid.v4()}
                className={classes.tableRow}
                onClick={() => onRowClicked && onRowClicked(row)}
                data-testid="tableRow"
              >
                {selectable && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      role="checkbox"
                      checked={isRowSelected}
                      onChange={(event) => handleClick(event, row.id)}
                    />
                  </TableCell>
                )}
                {headCells.map((cell) => (
                  <TableCell key={cell.id} style={cell.style}>
                    {cellValue(row[cell.id])}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

DesktopList.propTypes = {
  rowStyle: PropTypes.object,
  onRequestSort: PropTypes.func,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string,
  headCells: PropTypes.arrayOf(PropTypes.object).isRequired,
  bodyCells: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectable: PropTypes.bool,
  tableTitle: PropTypes.string,
  customClass: PropTypes.string,
  itemsCount: PropTypes.number,
  currentPage: PropTypes.number,
  onRowClicked: PropTypes.func,
};

export default DesktopList;
